import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { ChatMessage, ChatState } from '@/types';

/**
 * Chatbot context value interface.
 */
interface ChatbotContextValue extends ChatState {
  isVisible: boolean;
  toggleOpen: () => void;
  sendMessage: (text: string) => Promise<void>;
  clearMessages: () => void;
}

const ChatbotContext = createContext<ChatbotContextValue | null>(null);

/**
 * Hook to access chatbot context.
 *
 * Returns:
 *   ChatbotContextValue with state and actions.
 *
 * Raises:
 *   Error if used outside ChatbotProvider.
 */
export function useChatbot(): ChatbotContextValue {
  const context = useContext(ChatbotContext);
  if (!context) {
    throw new Error('useChatbot must be used within ChatbotProvider');
  }
  return context;
}

/**
 * Generates a unique message ID.
 */
function generateId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Parses SSE stream from OpenAI-compatible API.
 */
async function parseSSEStream(
  response: Response,
  onChunk: (text: string) => void
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) return;

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              onChunk(content);
            }
          } catch {
            // Ignore parse errors for malformed chunks
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/**
 * Props for ChatbotProvider.
 */
interface ChatbotProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for chatbot state and visibility logic.
 *
 * Manages:
 * - Chat message history
 * - Open/closed state
 * - Loading and error states
 * - Scroll-based visibility
 * - Route-based visibility
 */
export function ChatbotProvider({ children }: ChatbotProviderProps) {
  const location = useLocation();
  const [state, setState] = useState<ChatState>({
    messages: [],
    isOpen: false,
    isLoading: false,
    error: null,
  });
  const [scrollY, setScrollY] = useState(0);

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine visibility based on route and scroll
  const isVisible = useMemo(() => {
    const path = location.pathname;

    // Never show on thank-you page
    if (path === '/thank-you') return false;

    // Always show on contact page
    if (path === '/contact') return true;

    // Show on home after scrolling past hero (80vh)
    if (path === '/') {
      const heroHeight = window.innerHeight * 0.8;
      return scrollY > heroHeight;
    }

    // Other pages: show after scrolling 300px
    return scrollY > 300;
  }, [location.pathname, scrollY]);

  const toggleOpen = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: !prev.isOpen, error: null }));
  }, []);

  const clearMessages = useCallback(() => {
    setState((prev) => ({ ...prev, messages: [], error: null }));
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      error: null,
    }));

    // Create placeholder for assistant message
    const assistantId = generateId();
    setState((prev) => ({
      ...prev,
      messages: [
        ...prev.messages,
        { id: assistantId, role: 'assistant', content: '', timestamp: new Date() },
      ],
    }));

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: trimmed }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get response');
      }

      // Parse streaming response
      await parseSSEStream(response, (chunk) => {
        setState((prev) => ({
          ...prev,
          messages: prev.messages.map((msg) =>
            msg.id === assistantId ? { ...msg, content: msg.content + chunk } : msg
          ),
        }));
      });

      setState((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        // Remove empty assistant message on error
        messages: prev.messages.filter((msg) => msg.id !== assistantId || msg.content),
      }));
    }
  }, []);

  const value: ChatbotContextValue = {
    ...state,
    isVisible,
    toggleOpen,
    sendMessage,
    clearMessages,
  };

  return <ChatbotContext.Provider value={value}>{children}</ChatbotContext.Provider>;
}
